import React from "react";
import FormLabel from "./FormLabel";
import Dropzone from "react-dropzone";
import { FileText, Upload, Trash2, CheckCircle, XCircle, File } from "lucide-react";

const Uploader = (props) => {
  const { error, label, name = "uploader", onUpload, docTypeId, files = [] } = props;

  const handleDrop = (fileList) => {
    // Combine existing files with new files
    const combinedFiles = [...files, ...fileList];
    if (onUpload) {
      onUpload(combinedFiles, docTypeId);
    }
  };

  const handleDelete = (event, fileToDelete) => {
    event.stopPropagation();
    const newFiles = files.filter((f) => f !== fileToDelete);
    if (onUpload) {
      onUpload(newFiles, docTypeId);
    }
  };

  const handleRemoveAll = (event) => {
    event.stopPropagation();
    if (onUpload) {
      onUpload([], docTypeId);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const isPDF = (file) => {
    return file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
  };

  return (
    <div>
      {label && (
        <FormLabel
          text={label}
          name={name}
          color={error ? "red" : "black"}
          error={error}
        />
      )}
      
      <Dropzone onDrop={handleDrop}  accept={{'application/pdf': []}}>
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <div className="space-y-4">
              {/* Dropzone Area */}
              <div
                className={`
                  mt-2 flex justify-center px-6 py-8 border-2 border-dashed rounded-xl
                  transition-all duration-200 cursor-pointer
                  ${isDragActive 
                    ? "border-blue-500 bg-blue-50" 
                    : error 
                      ? "border-red-400 bg-red-50" 
                      : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
                  }
                `}
                {...getRootProps()}
              >
                <div className="text-center">
                  <div className={`
                    mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-4
                    ${isDragActive ? "bg-blue-100" : "bg-gray-200"}
                  `}>
                    <Upload className={`h-7 w-7 ${isDragActive ? "text-blue-600" : "text-gray-500"}`} />
                  </div>
                  
                  <input {...getInputProps()} />
                  
                  <p className="text-sm text-gray-600">
                    <button
                      type="button"
                      className="font-semibold text-blue-600 hover:text-blue-700 focus:outline-none focus:underline transition duration-150 ease-in-out"
                    >
                      Click to upload
                    </button>
                    <span className="text-gray-500"> or drag and drop</span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    PDF files only (max 10MB each)
                  </p>
                </div>
              </div>

              {/* Uploaded Files List */}
              {files.length > 0 && (
                <div className="space-y-3">
                  {/* Header with count and remove all */}
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-medium text-gray-700">
                      Uploaded Files ({files.length})
                    </h5>
                    <button
                      type="button"
                      onClick={handleRemoveAll}
                      className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Remove All
                    </button>
                  </div>

                  {/* Files List */}
                  <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden shadow-sm">
                    {files.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="p-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors duration-150"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {/* File Icon */}
                          <div className={`
                            w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                            ${isPDF(file) ? 'bg-red-100' : 'bg-amber-100'}
                          `}>
                            <FileText className={`w-5 h-5 ${isPDF(file) ? 'text-red-600' : 'text-amber-600'}`} />
                          </div>

                          {/* File Details */}
                          <div className="min-w-0 flex-1">
                            <p 
                              className="text-sm font-medium text-gray-900 truncate" 
                              title={file.name}
                            >
                              {file.name}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                              <span className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </span>
                              {isPDF(file) ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-600">
                                  <CheckCircle className="w-3 h-3" />
                                  Valid PDF
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-red-600">
                                  <XCircle className="w-3 h-3" />
                                  Invalid format
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, file)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-150 flex-shrink-0"
                          title="Remove file"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Validation Warning */}
                  {files.some(file => !isPDF(file)) && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                      <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Invalid files detected</p>
                        <p className="text-xs text-red-700 mt-0.5">
                          Please remove non-PDF files before submitting your request.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Success Message when all valid */}
                  {files.length > 0 && files.every(file => isPDF(file)) && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <p className="text-sm text-green-700">
                        All {files.length} file{files.length > 1 ? 's are' : ' is'} valid and ready to submit
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }}
      </Dropzone>
    </div>
  );
};

export default Uploader;